import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DataMatrixMapper, GroupLevel } from 'app/trello/components/shared/visualisations/daily-heatmap-chart-with-table/data-matrix-mapper';
import { HeatmapChartWithTableComponent } from 'app/trello/components/shared/visualisations/heatmap-chart-with-table/heatmap-chart-with-table.component';
import { LabelledMatrixValues } from 'app/trello/models/charts';
import { StatisticsAggregator } from 'app/trello/utility/statistics-aggregator';
import { StructureTransformer } from 'app/trello/utility/structure-transformer';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-date-range-heatmap-chart-with-table',
  templateUrl: './date-range-heatmap-chart-with-table.component.html',
  imports: [
    HeatmapChartWithTableComponent,
    NgIf,
  ]
})
export class DateRangeHeatmapChartWithTableComponent implements OnInit, OnChanges {

  @Input({required: true}) chartData!: { [date: string]: any[] } | { [date: string]: { [key: string]: any[] } };

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @Input() baseColor: string = '0,175,221';
  @Input() minDate?: string;
  @Input() maxDate?: string;
  @Input() maxValue?: number;

  @Input() tableTitle: string | undefined;

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  protected extractedMaxValue: number = 0;

  protected mappedChartData!: LabelledMatrixValues;

  protected tooltipFormatter!: ((x: number, y: number) => string);

  constructor(
    private dataMatrixMapper: DataMatrixMapper,
    private statisticsAggregator: StatisticsAggregator,
    private structureTransformer: StructureTransformer,
  ) {
  }

  public ngOnInit(): void {

    this.prepareData();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartData']) {
      this.prepareData();
    }
  }

  private prepareData(): void {

    if (!this.chartData) {
      return;
    }

    let stats: { [date: string]: any[] };

    const sampleValue = Object.values(this.chartData)[0];

    if (sampleValue && !Array.isArray(sampleValue)) {

      stats =
        this.structureTransformer.mergeInnerArrays2D(
          this.chartData as { [date: string]: { [key: string]: any[] } },
          true
        );

    } else {
      stats = this.chartData as { [date: string]: any[] };
    }

    const dailyStats = this.statisticsAggregator.aggregateCountDistinct(stats, 'day');

    const {groupLevel, data} = this.dataMatrixMapper.mapData(dailyStats, this.minDate, this.maxDate, true);

    const originalXLabels = [...data.xLabels];

    this.tooltipFormatter = (x: number, y: number): string => {

      const columnLabel = originalXLabels[x];

      const rowIndex = data.yLabels.length - 1 - y;

      const date = DateTime.fromISO(columnLabel).plus({days: rowIndex}).toISODate()!;
      const dayOfWeek = data.yLabels[rowIndex];

      return `${date} (${dayOfWeek})`;
    };

    data.xLabels = data.xLabels.map((formattedDate) => this.reformatDateAccordingToLevel(formattedDate, groupLevel));

    this.mappedChartData = data;

    this.extractedMaxValue = this.getMaxValue(data);
  }

  private getMaxValue(
    matrixValues: LabelledMatrixValues,
  ): number {

    let maxValue = Number.NEGATIVE_INFINITY;

    matrixValues.values.forEach((column) => {

      column.forEach((value) => {

        if (value > maxValue) {
          maxValue = value;
        }
      });
    });

    return maxValue;
  }

  private reformatDateAccordingToLevel(
    formattedDate: string,
    groupLevel: GroupLevel,
  ) {

    const date = DateTime.fromISO(formattedDate);

    switch (groupLevel) {

      case GroupLevel.WEEK: {

        const weekYear = date.weekYear;
        const weekNumber = date.weekNumber;

        return `${weekYear}-W${weekNumber}`;
      }

      case GroupLevel.MONTH: {
        return date.toFormat("LLLL yyyy");
      }

      case GroupLevel.QUARTER: {

        const quarter = Math.ceil(date.month / 3);

        return `${date.year}-Q${quarter}`;
      }

      case GroupLevel.HALF_YEAR: {

        const half = date.month <= 6 ? 1 : 2;

        return `${date.year}-H${half}`;
      }

      case GroupLevel.YEAR: {
        return `${date.year}`;
      }

      default: {
        return date.toISODate()!;
      }
    }
  }

}
